/* begin general import */

import Add16 from "@carbon/icons-react/es/add/16";
import OverflowMenuHorizontal24 from "@carbon/icons-react/es/overflow-menu--horizontal/24";
import SettingsAdjust16 from "@carbon/icons-react/es/settings--adjust/16";
import TrashCan16 from "@carbon/icons-react/es/trash-can/16";
import { Dropdown, Menu } from "antd";
import { ColumnProps } from "antd/lib/table";
import { ROLE_DETAIL_ROUTE, ROLE_ROUTE } from "config/route-consts";
import { Role, RoleFilter } from "models/Role";
import { Site, SiteFilter } from "models/Site";
import { Status } from "models/Status";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IdFilter } from "react3l-advanced-filters";
import {
  ActionBarComponent,
  AdvanceEnumFilterMaster,
  Button,
  LayoutCell,
  LayoutHeader,
  OneLineText,
  Pagination,
  StandardTable,
  StatusLine,
  TagFilter,
} from "react3l-ui-library";
import AdvanceIdFilterMaster from "react3l-ui-library/build/components/AdvanceFilterMaster/AdvanceIdFilterMaster";
import InputSearch from "react3l-ui-library/build/components/Input/InputSearch";
import { roleRepository } from "repositories/role-repository";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { masterService } from "services/page-services/master-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";
import RoleAdvanceFilter from "./RoleAdvanceFilter";
import { authorizationService } from "services/common-services/authorization-service";
import { API_ROLE_PREFIX } from "config/api-consts";

/* end individual import */

function RoleMaster() {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    ROLE_ROUTE.split("/")[ROLE_ROUTE.split("/").length - 1],
    API_ROLE_PREFIX
  );

  const [modelFilter, dispatch] = queryStringService.useQueryString(
    RoleFilter,
    { skip: 0, take: 10 }
  );
  const [visible, setVisible] = React.useState<boolean>(false);

  const {
    value: filter,
    handleChangeSelectFilter,
    handleChangeAllFilter,
    handleChangeInputSearch,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList, handleResetList } = listService.useList(
    roleRepository.list,
    roleRepository.count,
    filter,
    dispatch
  );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const {
    handleAction,
    handleBulkAction,
    canBulkAction,
    selectedRowKeys,
    setSelectedRowKeys,
  } = listService.useRowSelection(
    roleRepository.delete,
    roleRepository.bulkDelete,
    null,
    null,
    null,
    handleResetList
  );

  const { handleGoCreate, handleGoDetail, handleDeleteItem } =
    masterService.useMasterAction(ROLE_ROUTE, handleAction);

  const handleClone = React.useCallback(
    (id) => {
      roleRepository.clone(id).subscribe((item: Role) => {
        handleChangeAllFilter(filter);
      });
    },
    [filter, handleChangeAllFilter]
  );

  const handleCancel = React.useCallback(() => {
    setVisible(false);
  }, []);

  const menuAction = React.useCallback(
    (id: number, role: Role) => (
      <Menu>
        {validAction("clone") && (
          <Menu.Item key="1">
            <div
              className="ant-action-menu text-center"
              onClick={() => handleClone(id)}
            >
              {translate("roles.actions.clone")}
            </div>
          </Menu.Item>
        )}

        {validAction("update") && (
          <Menu.Item key="2">
            <div
              className="ant-action-menu text-center"
              onClick={handleGoDetail(id)}
            >
              {translate("general.actions.edit")}
            </div>
          </Menu.Item>
        )}
        {!role.used && validAction("delete") && (
          <Menu.Item key="3">
            <div
              className="ant-action-menu text-center"
              onClick={handleDeleteItem(role)}
            >
              {translate("general.actions.delete")}
            </div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [handleClone, handleDeleteItem, handleGoDetail, translate, validAction]
  );

  const handleReturnLink = React.useCallback((id) => {
    return `${ROLE_DETAIL_ROUTE}?id=${id}`;
  }, []);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup function to remove the event listener
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect runs only on mount and unmount

  const columns: ColumnProps<Role>[] = useMemo(
    () => [
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("roles.systemCode")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        width: 100,
        key: nameof(list[0].id),
        dataIndex: nameof(list[0].id),
        sorter: true,
        sortOrder: getAntOrderType<Role, RoleFilter>(
          filter,
          nameof(list[0].id)
        ),
        ellipsiss: true,
        render(...params: [string, Role, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={params[0]} countCharacters={20} />
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("roles.code")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        width: windowWidth < 1800 ? 300 : 500,
        key: nameof(list[0].code),
        dataIndex: nameof(list[0].code),
        sorter: true,
        sortOrder: getAntOrderType<Role, RoleFilter>(
          filter,
          nameof(list[0].code)
        ),
        ellipsiss: true,
        render(...params: [string, Role, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText
                value={params[0]}
                link={handleReturnLink(params[1].id)}
              />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "name"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("roles.name")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        sorter: true,
        sortOrder: getAntOrderType<Role, RoleFilter>(
          filter,
          nameof(list[0].name)
        ),
        ellipsiss: true,
        render(...params: [string, Role, number]) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText
                value={params[0]}
                link={handleReturnLink(params[1].id)}
              />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "site"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("roles.site")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        ellipsiss: true,
        key: nameof(list[0].site),
        dataIndex: nameof(list[0].site),
        sorter: true,
        width: 200,
        sortOrder: getAntOrderType<Role, RoleFilter>(
          filter,
          nameof(list[0].site)
        ),
        render(site: Site) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <OneLineText value={site?.name} />
            </LayoutCell>
          );
        },
      },

      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "status"
          );
          return (
            <LayoutHeader
              orderType="left"
              title={translate("roles.status")}
              sortedColumn={sortedColumn}
              isSorter
            />
          );
        },
        ellipsiss: true,
        key: nameof(list[0].status),
        dataIndex: nameof(list[0].status),
        sorter: true,
        width: 200,
        sortOrder: getAntOrderType<Role, RoleFilter>(
          filter,
          nameof(list[0].status)
        ),
        render(status: Status) {
          return (
            //fill the render field after generate code;
            <LayoutCell orderType="left" tableSize="md">
              <StatusLine value={status?.name} color={status?.color} />
            </LayoutCell>
          );
        },
      },

      {
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, role: Role) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Dropdown
                overlay={menuAction(id, role)}
                trigger={["click"]}
                placement="bottom"
                arrow
              >
                <OverflowMenuHorizontal24 className="btn-missions-blue" />
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [list, filter, translate, handleReturnLink, menuAction, windowWidth]
  );

  return (
    <>
      <div className="page-content page-content-dxg" id="page-content-dxg-id">
        <div className="info-component ml-4 mb-3">
          <span>{translate("roles.master.header")}</span>
          <span> &gt; </span>
          <span className="info-primary">
            {translate("roles.master.subHeader")}
          </span>
        </div>

        <div className="page page-master m-t--lg m-b--xxs">
          <div className="pt-4 pb-1 ml-3">
            <h5 className="font-bold page-title">
              {translate("roles.master.title")}
            </h5>
          </div>

          <div className="page-master__content">
            <div className="page-master__tag-filter p-t-xxs">
              <TagFilter
                value={filter}
                translate={translate}
                keyTranslate={"roles"}
                handleChangeFilter={handleChangeAllFilter}
                onClear={(value: any) => {
                  return 0;
                }}
              />
            </div>
            {(!selectedRowKeys || selectedRowKeys?.length === 0) && (
              <div className="page-master__filter-wrapper d-flex align-items-center justify-content-between px-3">
                <div className="page-master__filter d-flex align-items-center justify-content-start">
                  {validAction("filterListSite") && (
                    <div className="">
                      <AdvanceIdFilterMaster
                        value={filter[nameof(list[0].siteId)]["equal"]}
                        placeHolder={translate("roles.placeholder.site")}
                        classFilter={SiteFilter}
                        onChange={handleChangeSelectFilter({
                          fieldName: nameof(list[0].site),
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        getList={roleRepository.filterListSite}
                        label={translate("roles.site")}
                      />
                    </div>
                  )}
                  {validAction("filterListStatus") && (
                    <div className="">
                      <AdvanceEnumFilterMaster
                        value={filter[nameof(list[0].statusId)]["equal"]}
                        getList={roleRepository.filterListStatus}
                        onChange={handleChangeSelectFilter({
                          fieldName: nameof(list[0].status),
                          fieldType: "equal",
                          classFilter: IdFilter,
                        })}
                        label={translate("roles.status")}
                      />
                    </div>
                  )}
                </div>

                <div className="page-master__filter-action-search d-flex align-items-center">
                  <Button
                    type="icon-only-ghost"
                    icon={<SettingsAdjust16 />}
                    onClick={() => setVisible(true)}
                    className="btn--xl"
                  />
                  <InputSearch
                    value={filter.search}
                    classFilter={RoleFilter}
                    placeHolder={translate("general.placeholder.search")}
                    onChange={handleChangeInputSearch}
                    animationInput={filter?.search ? false : true}
                  />
                </div>

                <div className="page-master__actions  d-flex align-items-center justify-content-start">
                  <div className="page-master__filter-action d-flex align-items-center">
                    {validAction("create") && (
                      <Button
                        type="primary"
                        className="btn--lg btn-orange-wIcon ml-3"
                        icon={<Add16 />}
                        onClick={handleGoCreate}
                      >
                        {translate("general.actions.create")}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="page-master__content-table">
            {validAction("bulkDelete") && (
              <ActionBarComponent
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                translateTitleCancelButton={translate("general.actions.close")}
              >
                <Button
                  icon={<TrashCan16 />}
                  type="ghost-primary"
                  className="btn--lg"
                  disabled={!canBulkAction}
                  onClick={() => handleBulkAction(selectedRowKeys)}
                >
                  {translate("general.actions.delete")}
                </Button>
              </ActionBarComponent>
            )}
            <StandardTable
              rowKey={nameof(list[0].id)}
              columns={columns}
              dataSource={list}
              isDragable={true}
              tableSize={"md"}
              onChange={handleTableChange}
              spinning={loadingList}
              scroll={{ x: 1000 }}
            />

            <Pagination
              skip={filter?.skip}
              take={filter?.take}
              total={count}
              onChange={handlePagination}
            />
          </div>
        </div>
        {visible && (
          <RoleAdvanceFilter
            visible={visible}
            handleClose={handleCancel}
            visibleFooter={true}
            loading={false}
            size={"sm"}
            filter={filter}
            setVisible={setVisible}
            handleChangeAllFilter={handleChangeAllFilter}
          />
        )}
      </div>
    </>
  );
}
export default RoleMaster;
